import { parseISO } from 'date-fns';
import sortBy from 'lodash/sortBy';
import { ChangeEvent } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { Country } from 'common/src/constants/countries';
import { healZipPostalCodeValue } from 'common/src/transformers/healZipPostalCodeValue';
import { BillSource } from 'common/src/types/enums/BillSource.enum';
import { IAccount, IUtility } from 'common/src/types/interfaces';

import {
  getAddressFields,
  getStartAndEndDateFields,
} from 'portal/src/components/common/forms/commonFormFields';
import getInternalNotesField from 'portal/src/components/common/forms/internalNotesField';
import generateAllocationInfoTooltip from 'portal/src/components/common/generateAllocationInfoTooltip';

import {
  autoCompleteType,
  datePickerType,
  noFieldType,
  patternInputType,
  selectInputType,
  switchType,
  textInputType,
} from '../../../common/forms/FormFieldTypes';

const ACTIVE_ALLOCATION_TOOLTIP =
  'Warning: this account is used in an allocation. Changing this value may cause errors when allocating bills.';

const MASTER_ACCOUNT_TOOLTIP =
  'When enabled, bills for this account will be allocated to sub-accounts. An allocation template must be configured to use this account and all meters associated with the account must be configured as master meters.';

const SUB_ACCOUNT_TOOLTIP =
  'When set, this account will be designated as a sub-account for use in allocated bills. All meters associated with this account must be configured as sub-meters.';

export type AccountFormValues = ReturnType<typeof defaultValues>;

export const defaultValues = (entity: Partial<IAccount> = {}) => ({
  accountGroup: entity.accountGroup ?? '',
  address1: entity.address1 ?? '',
  address2: entity.address2 ?? '',
  associatedSupplyAccountId: entity.associatedSupplyAccountId ?? null,
  automationAccountNumber: entity.automationAccountNumber ?? '',
  city: entity.city ?? '',
  country: (entity.country ?? '') as Country,
  description: entity.description ?? '',
  department: entity.department ?? '',
  expectedBillSource: entity.expectedBillSource ?? '',
  glCode: entity.glCode ?? '',
  isMaster: !!entity.isMaster,
  masterAccountId: entity.masterAccountId ?? null,
  internalNotes: entity.internalNotes ?? '',
  notes: entity.notes ?? '',
  number: entity.number ?? '',
  postalCode: healZipPostalCodeValue(entity.postalCode, entity.country) ?? '',
  rateSchedule: entity.rateSchedule ?? '',
  serviceEndDate: entity.serviceEndDate
    ? parseISO(entity.serviceEndDate)
    : null,
  serviceStartDate: entity.serviceStartDate
    ? parseISO(entity.serviceStartDate)
    : null,
  state: entity.state ?? '',
  utilityId: entity.utilityId ?? '',
});

export const getFields = (
  accounts: Partial<IAccount>[],
  utilities: Partial<IUtility>[],
  masterAccounts: Partial<IAccount>[],
  hasSystemAdminAccess: boolean,
  showMasterAccountField: boolean,
  onRateScheduleChange: (e: ChangeEvent<HTMLInputElement>) => void,
  country: Country,
  setValue: UseFormSetValue<AccountFormValues>,
  hasActiveAllocationTemplate: boolean,
  openHistoricalValues?: () => void,
  allocationsEnabled: boolean = true,
) => {
  const formFieldsConfiguration: Array<
    | autoCompleteType
    | datePickerType
    | noFieldType
    | patternInputType
    | selectInputType
    | switchType
    | textInputType
  > = [
    {
      placeholder: 'Account Number',
      label: 'Account Number',
      name: 'number',
      type: 'textInput',
      required: true,
    },
    {
      placeholder: 'Utility',
      label: 'Utility',
      name: 'utilityId',
      type: 'autocomplete',
      required: true,
      options: sortBy(utilities, (u) => u.name.toLowerCase()).map((item) => ({
        label: item.name,
        value: item.id,
        disabled: !item.isActive,
        key: item.id,
      })),
    },
    ...getStartAndEndDateFields({
      startFieldName: 'serviceStartDate',
      endFieldName: 'serviceEndDate',
      startLabel: 'Service Start Date',
      endLabel: 'Service End Date',
    }),
    {
      placeholder: 'Account Group',
      label: 'Account Group',
      name: 'accountGroup',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Associated Supply Account',
      label: 'Associated Supply Account',
      name: 'associatedSupplyAccountId',
      type: 'autocomplete',
      required: false,
      options: sortBy(accounts, (a) => a.number.toLowerCase()).map((item) => ({
        label: item.number,
        value: item.id,
        disabled: !item.isActive,
        key: item.id,
      })),
    },
    {
      placeholder: 'Department',
      label: 'Department',
      name: 'department',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'G/L Code',
      label: 'G/L Code',
      name: 'glCode',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Rate Schedule',
      label: 'Rate Schedule',
      name: 'rateSchedule',
      type: 'textInput',
      required: false,
      onChange: onRateScheduleChange,
      helperAction: openHistoricalValues
        ? {
            label: 'See changes',
            onClick: openHistoricalValues,
          }
        : null,
    },
    {
      type: 'noField',
      name: 'noField',
    },
    ...getAddressFields(false, false, country),
    {
      placeholder: 'Automation Account Number',
      label: 'Automation Account Number',
      name: 'automationAccountNumber',
      type: 'textInput',
      required: false,
      hidden: !hasSystemAdminAccess,
    },
    {
      hidden: !hasSystemAdminAccess,
      label: 'Expected Bill Source',
      name: 'expectedBillSource',
      placeholder: 'Expected Bill Source',
      required: false,
      type: 'selectInput',
      values: Object.values(BillSource).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Account Description',
      label: 'Account Description',
      multiline: true,
      name: 'description',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Notes',
      label: 'Notes',
      multiline: true,
      name: 'notes',
      type: 'textInput',
      required: false,
    },
    getInternalNotesField(hasSystemAdminAccess),
    {
      disabled: !allocationsEnabled,
      infoTooltip: generateAllocationInfoTooltip(
        allocationsEnabled,
        hasActiveAllocationTemplate,
        ACTIVE_ALLOCATION_TOOLTIP,
        MASTER_ACCOUNT_TOOLTIP,
      ),
      label: 'Is this a master account?',
      name: 'isMaster',
      onChange: (newValue: string) => {
        if (Boolean(newValue)) {
          setValue('masterAccountId', null, { shouldDirty: true });
        }
      },
      required: false,
      type: 'switch',
      prependDivider: 'Allocations',
    },
    {
      disabled: !allocationsEnabled,
      hidden: !showMasterAccountField,
      infoTooltip: generateAllocationInfoTooltip(
        allocationsEnabled,
        hasActiveAllocationTemplate,
        ACTIVE_ALLOCATION_TOOLTIP,
        SUB_ACCOUNT_TOOLTIP,
      ),
      label: 'Master Account',
      name: 'masterAccountId',
      options: masterAccounts.map((item) => ({
        disabled: !item.isActive,
        key: item.id,
        label: item.number,
        value: item.id,
      })),
      placeholder: 'Master Account',
      required: false,
      type: 'autocomplete',
    },
    {
      type: 'noField',
      name: 'noField',
      hidden: showMasterAccountField,
    },
  ];

  return formFieldsConfiguration;
};
